import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Stack,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Divider,
  Badge,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { TableSkeleton } from '../components/Loading';
import ListPageEmpty from '../components/ListPageEmpty/ListPageEmpty';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { personsAPI } from '../services/api';
import { toast } from 'react-toastify';
import PersonEditDialog from '../components/PersonEditDialog/PersonEditDialog';
import PersonMergeDialog from '../components/PersonMerge/PersonMergeDialog';
import { usePermissions } from '../hooks/useAuth';

type PersonType = 'DIRECTOR' | 'SHAREHOLDER' | 'BENEFICIARY' | 'NOMINEE' | 'INDIVIDUAL' | 'TRUSTEE';

interface PersonFilters {
  types: PersonType[];
  nationality: string;
  hasEmail: boolean | null;
  hasPhone: boolean | null;
  search: string;
}

const People: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  
  const [persons, setPersons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dense, setDense] = useState(false);
  const [stats, setStats] = useState({ total: 0, directors: 0, shareholders: 0, beneficiaries: 0 });
  
  // Selection and actions
  const [selectedPersons, setSelectedPersons] = useState<GridRowSelectionModel>([]);
  const [bulkActionMenuAnchor, setBulkActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [rowActionMenuAnchor, setRowActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRowPerson, setSelectedRowPerson] = useState<any | null>(null);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<any | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [personToDelete, setPersonToDelete] = useState<any | null>(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTargetPerson, setMergeTargetPerson] = useState<any | null>(null);
  const [mergeSourcePersons, setMergeSourcePersons] = useState<any[]>([]);
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  
  // Filters
  const [filters, setFilters] = useState<PersonFilters>(() => {
    const types = searchParams.get('types')?.split(',').filter(Boolean) as PersonType[] || [];
    const nationality = searchParams.get('nationality') || '';
    const hasEmail = searchParams.get('hasEmail') === 'true' ? true : searchParams.get('hasEmail') === 'false' ? false : null;
    const hasPhone = searchParams.get('hasPhone') === 'true' ? true : searchParams.get('hasPhone') === 'false' ? false : null;
    const search = searchParams.get('search') || '';
    return { types, nationality, hasEmail, hasPhone, search };
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Available options for filters
  const availableNationalities = useMemo(() => {
    const nationalities = new Set<string>();
    persons.forEach(p => {
      if (p.nationality) nationalities.add(p.nationality);
    });
    return Array.from(nationalities).sort();
  }, [persons]);

  const loadPersons = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await personsAPI.getAll({ page: 1, limit: 5000 });
      const data = response.data?.data || response.data?.items || response.data || [];
      setPersons(data);

      const total = data.length;
      const directors = data.filter((p: any) => p.types?.includes('DIRECTOR')).length;
      const shareholders = data.filter((p: any) => p.types?.includes('SHAREHOLDER')).length;
      const beneficiaries = data.filter((p: any) => p.types?.includes('BENEFICIARY')).length;
      setStats({ total, directors, shareholders, beneficiaries });
    } catch (error) {
      console.error('Failed to load persons:', error);
      toast.error('Failed to load persons');
      setLoadError('Failed to load people. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPersons();
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.types.length > 0) {
      params.set('types', filters.types.join(','));
    }
    if (filters.nationality) {
      params.set('nationality', filters.nationality);
    }
    if (filters.hasEmail !== null) {
      params.set('hasEmail', filters.hasEmail.toString());
    }
    if (filters.hasPhone !== null) {
      params.set('hasPhone', filters.hasPhone.toString());
    }
    if (filters.search) {
      params.set('search', filters.search);
    }
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const filteredPersons = useMemo(() => {
    return persons.filter((person) => {
      // Text search
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          person.name?.toLowerCase().includes(searchLower) ||
          person.email?.toLowerCase().includes(searchLower) ||
          person.phone?.toLowerCase().includes(searchLower) ||
          person.types?.some((type: string) => type.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      // Type filter
      if (filters.types.length > 0) {
        const hasMatchingType = filters.types.some(type =>
          person.types?.includes(type)
        );
        if (!hasMatchingType) return false;
      }

      // Nationality filter
      if (filters.nationality) {
        if (person.nationality !== filters.nationality) return false;
      }

      // Has email filter
      if (filters.hasEmail !== null) {
        if (filters.hasEmail && !person.email) return false;
        if (!filters.hasEmail && person.email) return false;
      }

      // Has phone filter
      if (filters.hasPhone !== null) {
        if (filters.hasPhone && !person.phone) return false;
        if (!filters.hasPhone && person.phone) return false;
      }

      return true;
    });
  }, [persons, filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.types.length > 0) count++;
    if (filters.nationality) count++;
    if (filters.hasEmail !== null) count++;
    if (filters.hasPhone !== null) count++;
    if (filters.search) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    setFilters({
      types: [],
      nationality: '',
      hasEmail: null,
      hasPhone: null,
      search: '',
    });
  };

  const handleBulkAction = (action: string) => {
    if (selectedPersons.length === 0) {
      toast.warning('Please select at least one person');
      return;
    }

    switch (action) {
      case 'delete':
        if (window.confirm(`Delete ${selectedPersons.length} selected person(s)? This action cannot be undone.`)) {
          handleBulkDelete();
        }
        break;
      case 'export':
        handleBulkExport();
        break;
      case 'merge':
        if (selectedPersons.length < 2) {
          toast.warning('Please select at least 2 persons to merge');
          return;
        }
        const selectedData = persons.filter(p => selectedPersons.includes(p.id));
        setMergeTargetPerson(selectedData[0]);
        setMergeSourcePersons(selectedData.slice(1));
        setMergeDialogOpen(true);
        break;
    }
    setBulkActionMenuAnchor(null);
  };

  const handleBulkDelete = async () => {
    try {
      const deletePromises = selectedPersons.map(id => personsAPI.remove(String(id)));
      await Promise.all(deletePromises);
      toast.success(`Deleted ${selectedPersons.length} person(s)`);
      setSelectedPersons([]);
      // Reload persons
      const response = await personsAPI.getAll({ page: 1, limit: 5000 });
      const data = response.data?.data || response.data?.items || response.data || [];
      setPersons(data);
    } catch (error) {
      toast.error('Failed to delete persons');
      console.error(error);
    }
  };

  const handleBulkExport = () => {
    const selectedData = persons.filter(p => selectedPersons.includes(p.id));
    const csv = convertToCSV(selectedData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `persons-export-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedPersons.length} person(s)`);
  };

  const convertToCSV = (data: any[]): string => {
    if (data.length === 0) return '';
    const headers = ['Name', 'Email', 'Phone', 'Nationality', 'Types', 'Address'];
    const rows = data.map(person => [
      person.name || '',
      person.email || '',
      person.phone || '',
      person.nationality || '',
      person.types?.join(';') || '',
      person.address || '',
    ]);
    return [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
  };

  const handleRowAction = (event: React.MouseEvent<HTMLElement>, person: any) => {
    event.stopPropagation();
    setSelectedRowPerson(person);
    setRowActionMenuAnchor(event.currentTarget);
  };

  const handleEditPerson = (person?: any) => {
    setEditingPerson(person || null);
    setEditDialogOpen(true);
    setRowActionMenuAnchor(null);
  };

  const handleDeletePerson = (person: any) => {
    setPersonToDelete(person);
    setDeleteConfirmOpen(true);
    setRowActionMenuAnchor(null);
  };

  const confirmDelete = async () => {
    if (!personToDelete) return;
    try {
      await personsAPI.remove(personToDelete.id);
      toast.success('Person deleted');
      setDeleteConfirmOpen(false);
      setPersonToDelete(null);
      // Reload persons
      const response = await personsAPI.getAll({ page: 1, limit: 5000 });
      const data = response.data?.data || response.data?.items || response.data || [];
      setPersons(data);
    } catch (error) {
      toast.error('Failed to delete person');
      console.error(error);
    }
  };

  const handleDuplicatePerson = async (person: any) => {
    try {
      const duplicate = { ...person };
      delete duplicate.id;
      duplicate.name = `${person.name} (Copy)`;
      if (duplicate.email) {
        duplicate.email = `${duplicate.email.split('@')[0]}+copy@${duplicate.email.split('@')[1]}`;
      }
      await personsAPI.create(duplicate);
      toast.success('Person duplicated');
      setRowActionMenuAnchor(null);
      // Reload persons
      const response = await personsAPI.getAll({ page: 1, limit: 5000 });
      const data = response.data?.data || response.data?.items || response.data || [];
      setPersons(data);
    } catch (error) {
      toast.error('Failed to duplicate person');
      console.error(error);
    }
  };

  const handlePersonSaved = async () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setEditingPerson(null);
    await loadPersons();
  };

  const handleCellEdit = (id: string, field: string, value: any) => {
    setEditingCell({ id, field });
    setEditingValue(value || '');
  };

  const handleCellSave = async (id: string, field: string) => {
    if (!hasPermission('persons:update')) {
      toast.error('You do not have permission to update persons');
      setEditingCell(null);
      return;
    }

    try {
      await personsAPI.update(id, { [field]: editingValue });
      toast.success('Updated successfully');
      setEditingCell(null);
      // Update local state
      setPersons(persons.map(p => p.id === id ? { ...p, [field]: editingValue } : p));
    } catch (error) {
      toast.error('Failed to update');
      console.error(error);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const renderMobileCards = () => {
    if (loading) {
      return <TableSkeleton rows={6} />;
    }
    if (loadError) return null;
    if (filteredPersons.length === 0) {
      return (
        <Box sx={{ height: 400 }}>
          <ListPageEmpty
            message={activeFilterCount > 0 ? 'No people match your filters' : 'No people yet'}
            secondary={activeFilterCount > 0 ? 'Clear filters to see all people.' : 'Add people to get started.'}
            hasActiveFilters={activeFilterCount > 0}
            onClearFilters={handleClearFilters}
            icon={<PeopleIcon />}
            primaryAction={
              hasPermission('persons:create') && activeFilterCount === 0 ? (
                <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateDialogOpen(true)}>
                  Create First Person
                </Button>
              ) : undefined
            }
          />
        </Box>
      );
    }

    return (
      <Stack spacing={2} sx={{ pb: 2 }}>
        {filteredPersons.map((person) => {
          const initials = person.name
            ?.split(' ')
            .map((n: string) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase() || '?';

          return (
            <Card
              key={person.id}
              sx={{
                borderRadius: 2,
                '&:hover': { boxShadow: 3 },
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                  <Checkbox
                    checked={selectedPersons.includes(person.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectedPersons([...selectedPersons, person.id]);
                      } else {
                        setSelectedPersons(selectedPersons.filter((id) => id !== person.id));
                      }
                    }}
                    sx={{ mt: -0.5, ml: -0.5 }}
                  />
                  <Avatar
                    src={person.profilePhotoUrl ? personsAPI.getProfilePhotoUrl(person.id) : undefined}
                    sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: '0.875rem' }}
                  >
                    {initials}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      component={RouterLink}
                      to={`/persons/${person.id}`}
                      sx={{ textDecoration: 'none', color: 'primary.main', display: 'block' }}
                    >
                      {person.name}
                    </Typography>
                    {person.types && person.types.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {person.types.map((type: string) => (
                          <Chip key={type} label={type} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <IconButton
                    aria-label="Person actions"
                    size="small"
                    onClick={(e) => handleRowAction(e, person)}
                    sx={{ mt: -0.5, mr: -0.5 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Stack spacing={1} sx={{ ml: 7 }}>
                  {person.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                        Email:
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {person.email}
                      </Typography>
                    </Box>
                  )}
                  {person.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                        Phone:
                      </Typography>
                      <Typography variant="body2">{person.phone}</Typography>
                    </Box>
                  )}
                  {person.nationality && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                        Nationality:
                      </Typography>
                      <Typography variant="body2">{person.nationality}</Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                <Button
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to={`/persons/${person.id}`}
                  endIcon={<OpenInNewIcon />}
                  fullWidth
                >
                  View Details
                </Button>
              </CardActions>
            </Card>
          );
        })}
      </Stack>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'select',
      headerName: '',
      width: 50,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          indeterminate={selectedPersons.length > 0 && selectedPersons.length < filteredPersons.length}
          checked={filteredPersons.length > 0 && selectedPersons.length === filteredPersons.length}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedPersons(filteredPersons.map(p => p.id));
            } else {
              setSelectedPersons([]);
            }
          }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedPersons.includes(params.row.id)}
          onChange={(e) => {
            e.stopPropagation();
            if (e.target.checked) {
              setSelectedPersons([...selectedPersons, params.row.id]);
            } else {
              setSelectedPersons(selectedPersons.filter(id => id !== params.row.id));
            }
          }}
        />
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 250,
      sortable: true,
      editable: hasPermission('persons:update'),
      renderCell: (params) => {
        const isEditing = editingCell?.id === params.row.id && editingCell?.field === 'name';
        const initials = params.value
          ?.split(' ')
          .map((n: string) => n[0])
          .join('')
          .slice(0, 2)
          .toUpperCase() || '?';
        
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Avatar
                src={params.row.profilePhotoUrl ? personsAPI.getProfilePhotoUrl(params.row.id) : undefined}
                sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}
              >
                {initials}
              </Avatar>
              <TextField
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={() => handleCellSave(params.row.id, 'name')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCellSave(params.row.id, 'name');
                  } else if (e.key === 'Escape') {
                    handleCellCancel();
                  }
                }}
                autoFocus
                size="small"
                sx={{ flex: 1 }}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
          );
        }
        
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', cursor: hasPermission('persons:update') ? 'pointer' : 'default' }}
            onClick={(e) => {
              if (hasPermission('persons:update')) {
                e.stopPropagation();
                handleCellEdit(params.row.id, 'name', params.value);
              }
            }}
          >
            <RouterLink
              to={`/persons/${params.row.id}`}
              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar
                src={params.row.profilePhotoUrl ? personsAPI.getProfilePhotoUrl(params.row.id) : undefined}
                sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}
              >
                {initials}
              </Avatar>
            </RouterLink>
            <span style={{ fontWeight: 500, flex: 1 }}>{params.value}</span>
            {hasPermission('persons:update') && (
              <EditIcon sx={{ fontSize: 14, opacity: 0.5, '&:hover': { opacity: 1 } }} />
            )}
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      sortable: true,
      editable: hasPermission('persons:update'),
      renderCell: (params) => {
        const isEditing = editingCell?.id === params.row.id && editingCell?.field === 'email';
        
        if (isEditing) {
          return (
            <TextField
              type="email"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={() => handleCellSave(params.row.id, 'email')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCellSave(params.row.id, 'email');
                } else if (e.key === 'Escape') {
                  handleCellCancel();
                }
              }}
              autoFocus
              size="small"
              fullWidth
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', cursor: hasPermission('persons:update') ? 'pointer' : 'default' }}
            onClick={(e) => {
              if (hasPermission('persons:update')) {
                e.stopPropagation();
                handleCellEdit(params.row.id, 'email', params.value);
              }
            }}
          >
            <span style={{ flex: 1 }}>{params.value || '—'}</span>
            {hasPermission('persons:update') && (
              <EditIcon sx={{ fontSize: 14, opacity: 0.5, '&:hover': { opacity: 1 } }} />
            )}
          </Box>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'types',
      headerName: 'Types',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {params.value?.map((type: string) => (
            <Chip key={type} label={type} size="small" variant="outlined" />
          ))}
        </Box>
      ),
    },
    {
      field: 'nationality',
      headerName: 'Nationality',
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="Person actions"
          size="small"
          onClick={(e) => handleRowAction(e, params.row)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Enhanced Header with Actions */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 2,
          p: 3,
          mb: 3,
          color: 'white',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <PeopleIcon sx={{ fontSize: 36 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 0.5 }}>
                People Management
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Browse all persons including directors, shareholders, and beneficiaries
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            {hasPermission('persons:create') && (
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                }}
              >
                Create Person
              </Button>
            )}
            {selectedPersons.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={(e) => setBulkActionMenuAnchor(e.currentTarget)}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  Actions ({selectedPersons.length})
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48 }}>
                  <WorkIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.directors}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Directors
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.shareholders}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Shareholders
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48 }}>
                  <EmailIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.beneficiaries}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Beneficiaries
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
        }}
      >
        <Stack spacing={2}>
          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search people by name, email, phone, or type..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: filters.search && (
                <InputAdornment position="end">
                  <IconButton aria-label="Clear search" size="small" onClick={() => setFilters({ ...filters, search: '' })}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
              },
            }}
          />

          {/* Filter Chips */}
          {activeFilterCount > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Active filters:
              </Typography>
              {filters.types.map(type => (
                <Chip
                  key={type}
                  label={`Type: ${type}`}
                  size="small"
                  onDelete={() => setFilters({ ...filters, types: filters.types.filter(t => t !== type) })}
                />
              ))}
              {filters.nationality && (
                <Chip
                  label={`Nationality: ${filters.nationality}`}
                  size="small"
                  onDelete={() => setFilters({ ...filters, nationality: '' })}
                />
              )}
              {filters.hasEmail !== null && (
                <Chip
                  label={filters.hasEmail ? 'Has Email' : 'No Email'}
                  size="small"
                  onDelete={() => setFilters({ ...filters, hasEmail: null })}
                />
              )}
              {filters.hasPhone !== null && (
                <Chip
                  label={filters.hasPhone ? 'Has Phone' : 'No Phone'}
                  size="small"
                  onDelete={() => setFilters({ ...filters, hasPhone: null })}
                />
              )}
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{ ml: 'auto' }}
              >
                Clear All
              </Button>
            </Box>
          )}

          {/* Advanced Filters */}
          <Box>
            <Button
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              size="small"
            >
              Advanced Filters
              {activeFilterCount > 0 && (
                <Badge badgeContent={activeFilterCount} color="primary" sx={{ ml: 1 }}>
                  <Box sx={{ width: 0 }} />
                </Badge>
              )}
            </Button>
            {filtersExpanded && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      multiple
                      options={['DIRECTOR', 'SHAREHOLDER', 'BENEFICIARY', 'NOMINEE', 'INDIVIDUAL', 'TRUSTEE'] as PersonType[]}
                      value={filters.types}
                      onChange={(_, value) => setFilters({ ...filters, types: value })}
                      renderInput={(params) => <TextField {...params} label="Person Type" size="small" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} />
                        ))
                      }
                      sx={isMobile ? { '& .MuiAutocomplete-tag': { maxWidth: '100%' } } : undefined}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Nationality</InputLabel>
                      <Select
                        value={filters.nationality}
                        onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
                        label="Nationality"
                      >
                        <MenuItem value="">All Nationalities</MenuItem>
                        {availableNationalities.map(nat => (
                          <MenuItem key={nat} value={nat}>{nat}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Has Email</InputLabel>
                      <Select
                        value={filters.hasEmail === null ? '' : filters.hasEmail ? 'yes' : 'no'}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : e.target.value === 'yes';
                          setFilters({ ...filters, hasEmail: value });
                        }}
                        label="Has Email"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Has Phone</InputLabel>
                      <Select
                        value={filters.hasPhone === null ? '' : filters.hasPhone ? 'yes' : 'no'}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : e.target.value === 'yes';
                          setFilters({ ...filters, hasPhone: value });
                        }}
                        label="Has Phone"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Stack>
      </Paper>

      {loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadPersons}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {loadError}
        </Alert>
      )}

      {isMobile ? (
        renderMobileCards()
      ) : (
        <Paper
          elevation={3}
          sx={{
            height: 600,
            width: '100%',
            overflow: 'hidden',
            borderRadius: 2,
          }}
        >
          {loading ? (
            <TableSkeleton rows={10} />
          ) : loadError ? null : filteredPersons.length === 0 ? (
            <ListPageEmpty
              message={activeFilterCount > 0 ? 'No people match your filters' : 'No people yet'}
              secondary={activeFilterCount > 0 ? 'Clear filters to see all people.' : 'Add people to get started.'}
              hasActiveFilters={activeFilterCount > 0}
              onClearFilters={handleClearFilters}
              icon={<PeopleIcon />}
              primaryAction={
                hasPermission('persons:create') && activeFilterCount === 0 ? (
                  <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateDialogOpen(true)}>
                    Create First Person
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Chip
                  label={dense ? 'Compact' : 'Standard'}
                  size="small"
                  variant={dense ? 'filled' : 'outlined'}
                  onClick={() => setDense(!dense)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <DataGrid
                rows={filteredPersons}
                columns={columns}
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                disableRowSelectionOnClick
                checkboxSelection={false}
                rowSelectionModel={selectedPersons}
                onRowSelectionModelChange={setSelectedPersons}
                density={dense ? 'compact' : 'standard'}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-row:hover': {
                    bgcolor: 'action.hover',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    bgcolor: 'grey.50',
                    borderBottom: 2,
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                  },
                }}
              />
            </>
          )}
        </Paper>
      )}

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkActionMenuAnchor}
        open={Boolean(bulkActionMenuAnchor)}
        onClose={() => setBulkActionMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkAction('export')}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export {selectedPersons.length} selected</ListItemText>
        </MenuItem>
        {hasPermission('persons:manage') && (
          <MenuItem onClick={() => handleBulkAction('merge')}>
            <ListItemIcon><MergeTypeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Merge selected</ListItemText>
          </MenuItem>
        )}
        <Divider />
        {hasPermission('persons:delete') && (
          <MenuItem
            onClick={() => handleBulkAction('delete')}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete {selectedPersons.length} selected</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Row Actions Menu */}
      <Menu
        anchorEl={rowActionMenuAnchor}
        open={Boolean(rowActionMenuAnchor)}
        onClose={() => {
          setRowActionMenuAnchor(null);
          setSelectedRowPerson(null);
        }}
      >
        <MenuItem onClick={() => {
          if (selectedRowPerson) {
            navigate(`/persons/${selectedRowPerson.id}`);
          }
          setRowActionMenuAnchor(null);
        }}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {hasPermission('persons:update') && (
          <MenuItem onClick={() => {
            if (selectedRowPerson) {
              handleEditPerson(selectedRowPerson);
            }
          }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {hasPermission('persons:create') && (
          <MenuItem onClick={() => {
            if (selectedRowPerson) {
              handleDuplicatePerson(selectedRowPerson);
            }
          }}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>
        )}
        {hasPermission('persons:manage') && (
          <MenuItem onClick={() => {
            if (selectedRowPerson) {
              setMergeTargetPerson(selectedRowPerson);
              setMergeSourcePersons([]);
              setMergeDialogOpen(true);
            }
            setRowActionMenuAnchor(null);
          }}>
            <ListItemIcon><MergeTypeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Combine with Another Person</ListItemText>
          </MenuItem>
        )}
        {hasPermission('persons:delete') && (
          <MenuItem
            onClick={() => {
              if (selectedRowPerson) {
                handleDeletePerson(selectedRowPerson);
              }
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create Person Dialog */}
      <PersonEditDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handlePersonSaved}
        showAdvancedFields={false}
      />

      {/* Edit Person Dialog */}
      <PersonEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingPerson(null);
        }}
        person={editingPerson}
        onSave={handlePersonSaved}
        showAdvancedFields={false}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Person</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{personToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Dialog */}
      <PersonMergeDialog
        open={mergeDialogOpen}
        onClose={() => {
          setMergeDialogOpen(false);
          setMergeTargetPerson(null);
          setMergeSourcePersons([]);
        }}
        targetPerson={mergeTargetPerson || undefined}
        sourcePersons={mergeSourcePersons}
        onComplete={async () => {
          // Reload persons after merge
          const response = await personsAPI.getAll({ page: 1, limit: 5000 });
          const data = response.data?.data || response.data?.items || response.data || [];
          setPersons(data);
          setSelectedPersons([]);
        }}
      />
    </Container>
  );
};

export default People;
